import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import BarChart from '@cloudscape-design/components/bar-chart';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import DatePicker from '@cloudscape-design/components/date-picker';
import Header from '@cloudscape-design/components/header';
import LineChart from '@cloudscape-design/components/line-chart';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import Toggle from '@cloudscape-design/components/toggle';
import type {
  ComputeSubscriptionReceivablesResult,
  Currency,
  ReceivablesWeekBucket,
} from '@etiquetachef/types';
import { computeSubscriptionReceivablesCallable } from '@/lib/callables';
import { formatCurrencyCents } from '@/features/dashboard-finance/formatters';
import { dayBoundMs } from '@/features/finance-analysis/financeAnalysisFormat';

const CURRENCIES: readonly Currency[] = ['BRL', 'EUR', 'USD'];

/** Totais de um bucket numa moeda (`ReceivablesTotals`, todos em centavos). */
type Totals = ReceivablesWeekBucket['byCurrency'][string];

/** Totais do bucket na moeda; `undefined` se a semana não teve nada nela. */
function bucketTotals(b: ReceivablesWeekBucket, currency: Currency): Totals | undefined {
  return b.byCurrency[currency];
}

/**
 * Tela `/billing/subscriptions`: recebíveis de assinaturas por semana —
 * previsto × realizado × em aberto — com KPIs, tabela e gráficos.
 *
 * Origem: #78 — 2026-06-22 10:29 +0200 (tela Assinaturas — recebíveis
 * semanais previsto × realizado).
 *
 * Por quê: a operação vê quanto vai entrar e quanto já entrou sem abrir o
 * painel do PSP. Só a JANELA (`from`/`to`) chama o servidor; moeda e
 * "esconder semanas zeradas" filtram no cliente sobre o payload, que já
 * traz todas as moedas.
 *
 * Contrato: callable `computeSubscriptionReceivables` (shim
 * `requireSuperAdmin` — qualquer papel admin, leitura; `docs/permissions.md`)
 * com `from`/`to` em ms epoch (limites do dia local via `dayBoundMs`;
 * vazios = janela default do servidor). Falha → `Alert` de erro (só
 * `console.error`). `capped: true` → aviso de dados incompletos. Sem
 * escrita. Resposta que chega depois de a janela mudar é descartada
 * (`alive`).
 *
 * Usado por: `routes/router.tsx` (rota `billing/subscriptions`, dentro do
 * `RequireSuperAdmin`), menu Cobrança → "Assinaturas" (`AdminLayout`). Só
 * `apps/admin` (web). Spec: `docs/screens/subscriptions-receivables.md`.
 *
 * Arquitetura: a agregação é do servidor (três `collectionGroup` com Admin
 * SDK — `billing`, `payments`, `invoices`), sobre o pricing congelado de
 * `billing/subscription`; a tela não lê Firestore.
 *
 * Dados: `ComputeSubscriptionReceivablesResult`
 * (`packages/types/src/receivables.ts`). Valores em CENTAVOS
 * (`forecastCents`, `realizedCents`, `outstandingCents`, `mrrByCurrency`),
 * formatados por `formatCurrencyCents` na moeda escolhida (BRL | EUR |
 * USD); semanas ISO com corte UTC (`periodKey` = segunda `AAAA-MM-DD`):
 *   { "windowStart": 1780000000000, "windowEnd": 1790000000000,
 *     "now": 1785000000000, "activeSubscriptions": 3, "capped": false,
 *     "mrrByCurrency": { "BRL": 34800 },
 *     "totalsByCurrency": { "BRL": { "forecastCents": 34800,
 *       "forecastMonthlyCents": 34800, "forecastYearlyCents": 0,
 *       "realizedCents": 0, "outstandingCents": 0, "subscriptionCount": 1 } },
 *     "buckets": [ { "periodKey": "2026-10-05", "periodStart": 1785000000000,
 *       "isForecast": false, "isCurrent": true, "byCurrency": { … } } ] }
 * Sem conversão de câmbio: cada moeda é vista separadamente.
 *
 * Falhas conhecidas: as do cálculo (#130 zumbi sem gateway, #258 TEST MODE,
 * #738 só receita direta) vivem na callable — ver a docstring dela.
 *
 * Issues: #78 (origem), #130, #258, #738.
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function BillingSubscriptionsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [currency, setCurrency] = useState<Currency | ''>('');
  const [hideZero, setHideZero] = useState(false);
  const [result, setResult] = useState<ComputeSubscriptionReceivablesResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Só a JANELA (from/to) recomputa via callable; moeda/hide-zero são
  // client-side sobre o payload (o backend devolve todas as moedas).
  useEffect(() => {
    const fromMs = from !== '' ? dayBoundMs(from, false) : undefined;
    const toMs = to !== '' ? dayBoundMs(to, true) : undefined;
    let alive = true;
    setLoading(true);
    setError(false);
    computeSubscriptionReceivablesCallable({
      ...(fromMs !== undefined ? { from: fromMs } : {}),
      ...(toMs !== undefined ? { to: toMs } : {}),
    })
      .then((r) => {
        if (alive) setResult(r);
      })
      .catch((err: unknown) => {
        console.error('computeSubscriptionReceivables failed', err);
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return (): void => {
      alive = false;
    };
  }, [from, to]);

  const currenciesInResult = useMemo<Currency[]>(() => {
    const set = new Set<Currency>();
    for (const c of Object.keys(result?.totalsByCurrency ?? {})) set.add(c as Currency);
    for (const c of Object.keys(result?.mrrByCurrency ?? {})) set.add(c as Currency);
    return CURRENCIES.filter((c) => set.has(c));
  }, [result]);

  const effectiveCurrency: Currency =
    currency !== '' && currenciesInResult.includes(currency)
      ? currency
      : (currenciesInResult[0] ?? 'BRL');

  const buckets = useMemo(() => result?.buckets ?? [], [result]);
  const rows = useMemo(
    () =>
      hideZero
        ? buckets.filter((b) => {
            const tot = bucketTotals(b, effectiveCurrency);
            return (
              tot !== undefined &&
              (tot.forecastCents !== 0 || tot.realizedCents !== 0 || tot.outstandingCents !== 0)
            );
          })
        : buckets,
    [buckets, effectiveCurrency, hideZero],
  );

  const fmt = (v: number): string => formatCurrencyCents(v, effectiveCurrency);
  const totals = result?.totalsByCurrency[effectiveCurrency];
  const mrr = result?.mrrByCurrency[effectiveCurrency] ?? 0;
  const nextWeekForecast = useMemo(() => {
    const next = buckets.find((b) => b.isForecast);
    return next ? (bucketTotals(next, effectiveCurrency)?.forecastCents ?? 0) : 0;
  }, [buckets, effectiveCurrency]);

  const currencyOptions: SelectProps.Option[] = (
    currenciesInResult.length > 0 ? currenciesInResult : CURRENCIES
  ).map((c) => ({ value: c, label: c }));

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('admin.subscriptions.subtitle')}>
          {t('admin.subscriptions.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Painel KPI (reflete moeda + janela) */}
        <Container header={<Header variant="h2">{t('admin.subscriptions.summary')}</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <Kpi label={t('admin.subscriptions.kpi.mrr')} value={fmt(mrr)} />
            <Kpi
              label={t('admin.subscriptions.kpi.forecast')}
              value={fmt(totals?.forecastCents ?? 0)}
            />
            <Kpi
              label={t('admin.subscriptions.kpi.realized')}
              value={fmt(totals?.realizedCents ?? 0)}
              color="text-status-success"
            />
            <Kpi
              label={t('admin.subscriptions.kpi.outstanding')}
              value={fmt(totals?.outstandingCents ?? 0)}
              color="text-status-error"
            />
            <Kpi
              label={t('admin.subscriptions.kpi.activeSubscriptions')}
              value={String(result?.activeSubscriptions ?? 0)}
            />
            <Kpi label={t('admin.subscriptions.kpi.nextWeek')} value={fmt(nextWeekForecast)} />
          </ColumnLayout>
        </Container>

        {/* Filtros compartilhados */}
        <Container>
          <SpaceBetween size="xs" direction="horizontal">
            <DatePicker
              value={from}
              onChange={({ detail }): void => {
                setFrom(detail.value);
              }}
              placeholder={t('admin.subscriptions.filters.from')}
            />
            <DatePicker
              value={to}
              onChange={({ detail }): void => {
                setTo(detail.value);
              }}
              placeholder={t('admin.subscriptions.filters.to')}
            />
            <Select
              selectedOption={currencyOptions.find((o) => o.value === effectiveCurrency) ?? null}
              options={currencyOptions}
              onChange={({ detail }): void => {
                setCurrency((detail.selectedOption.value as Currency | undefined) ?? '');
              }}
            />
            <Toggle
              checked={hideZero}
              onChange={({ detail }): void => {
                setHideZero(detail.checked);
              }}
            >
              {t('admin.subscriptions.filters.hideZero')}
            </Toggle>
          </SpaceBetween>
        </Container>

        {error && <Alert type="error">{t('admin.subscriptions.errorLoad')}</Alert>}
        {result?.capped === true && (
          <Alert type="warning">{t('admin.subscriptions.capped')}</Alert>
        )}
        {!loading && buckets.length === 0 && !error && (
          <Alert type="info">{t('admin.subscriptions.empty')}</Alert>
        )}

        <Tabs
          tabs={[
            {
              id: 'table',
              label: t('admin.subscriptions.tabs.table'),
              content: (
                <ReceivablesTable rows={rows} currency={effectiveCurrency} fmt={fmt} />
              ),
            },
            {
              id: 'flow',
              label: t('admin.subscriptions.tabs.flow'),
              content: <WeeklyFlowChart rows={rows} currency={effectiveCurrency} fmt={fmt} />,
            },
            {
              id: 'cumulative',
              label: t('admin.subscriptions.tabs.cumulative'),
              content: <CumulativeChart rows={rows} currency={effectiveCurrency} fmt={fmt} />,
            },
            {
              id: 'byCurrency',
              label: t('admin.subscriptions.tabs.byCurrency'),
              content: (
                <ByCurrencyChart buckets={buckets} currencies={currenciesInResult} />
              ),
            },
            {
              id: 'byCycle',
              label: t('admin.subscriptions.tabs.byCycle'),
              content: <ByCycleChart rows={rows} currency={effectiveCurrency} fmt={fmt} />,
            },
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}

/** Bloco de KPI (rótulo + valor grande, cor opcional). */
function Kpi({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: 'text-status-success' | 'text-status-error';
}): React.JSX.Element {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="awsui-value-large" {...(color !== undefined ? { color } : {})}>
        {value}
      </Box>
    </div>
  );
}

/** Rótulo da semana + badge de estado (atual / previsto). */
function PeriodCell({ b }: { b: ReceivablesWeekBucket }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <SpaceBetween size="xs" direction="horizontal">
      <span>{b.periodKey}</span>
      {b.isCurrent && <Badge color="green">{t('admin.subscriptions.badge.current')}</Badge>}
      {b.isForecast && <Badge color="blue">{t('admin.subscriptions.badge.forecast')}</Badge>}
    </SpaceBetween>
  );
}

/** Tabela semanal: previsto, realizado, em aberto e nº de cobranças na moeda. */
function ReceivablesTable({
  rows,
  currency,
  fmt,
}: {
  rows: ReceivablesWeekBucket[];
  currency: Currency;
  fmt: (v: number) => string;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Table<ReceivablesWeekBucket>
      items={rows}
      trackBy="periodKey"
      variant="embedded"
      empty={
        <Box textAlign="center" color="inherit">
          {t('admin.subscriptions.empty')}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'period',
          header: t('admin.subscriptions.columns.period'),
          cell: (b): React.ReactNode => <PeriodCell b={b} />,
        },
        {
          id: 'forecast',
          header: t('admin.subscriptions.columns.forecast'),
          cell: (b): React.ReactNode => (
            <Box textAlign="right">{fmt(bucketTotals(b, currency)?.forecastCents ?? 0)}</Box>
          ),
        },
        {
          id: 'realized',
          header: t('admin.subscriptions.columns.realized'),
          cell: (b): React.ReactNode => (
            <Box textAlign="right">{fmt(bucketTotals(b, currency)?.realizedCents ?? 0)}</Box>
          ),
        },
        {
          id: 'outstanding',
          header: t('admin.subscriptions.columns.outstanding'),
          cell: (b): React.ReactNode => (
            <Box textAlign="right">{fmt(bucketTotals(b, currency)?.outstandingCents ?? 0)}</Box>
          ),
        },
        {
          id: 'subs',
          header: t('admin.subscriptions.columns.subscriptions'),
          cell: (b): React.ReactNode => (
            <Box textAlign="right">{bucketTotals(b, currency)?.subscriptionCount ?? 0}</Box>
          ),
        },
      ]}
    />
  );
}

const CHART_COMMON = {
  xScaleType: 'categorical' as const,
  height: 260,
  hideFilter: true,
};

/** Estado vazio dos gráficos Cloudscape. */
function emptyBox(text: string): React.JSX.Element {
  return (
    <Box textAlign="center" color="inherit">
      {text}
    </Box>
  );
}

/** Barras por semana: realizado (passado/atual) × previsto (futuro). */
function WeeklyFlowChart({
  rows,
  currency,
  fmt,
}: {
  rows: ReceivablesWeekBucket[];
  currency: Currency;
  fmt: (v: number) => string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const realized = rows.map((b) => ({
    x: b.periodKey,
    y: b.isForecast ? 0 : (bucketTotals(b, currency)?.realizedCents ?? 0),
  }));
  const forecast = rows.map((b) => ({
    x: b.periodKey,
    y: b.isForecast || b.isCurrent ? (bucketTotals(b, currency)?.forecastCents ?? 0) : 0,
  }));
  return (
    <BarChart
      {...CHART_COMMON}
      ariaLabel={t('admin.subscriptions.tabs.flow')}
      i18nStrings={{ yTickFormatter: fmt }}
      series={[
        { title: t('admin.subscriptions.columns.realized'), type: 'bar', data: realized },
        { title: t('admin.subscriptions.columns.forecast'), type: 'bar', data: forecast },
      ]}
      empty={emptyBox(t('admin.subscriptions.empty'))}
    />
  );
}

/** Linha do recebível acumulado (realizado no passado, somando previsto à frente). */
function CumulativeChart({
  rows,
  currency,
  fmt,
}: {
  rows: ReceivablesWeekBucket[];
  currency: Currency;
  fmt: (v: number) => string;
}): React.JSX.Element {
  const { t } = useTranslation();
  let acc = 0;
  const data = rows.map((b) => {
    const tot = bucketTotals(b, currency);
    const add = b.isForecast
      ? (tot?.forecastCents ?? 0)
      : (tot?.realizedCents ?? 0) + (b.isCurrent ? (tot?.forecastCents ?? 0) : 0);
    acc += add;
    return { x: b.periodKey, y: acc };
  });
  return (
    <LineChart
      {...CHART_COMMON}
      hideLegend
      ariaLabel={t('admin.subscriptions.tabs.cumulative')}
      i18nStrings={{ yTickFormatter: fmt }}
      series={[
        {
          title: t('admin.subscriptions.tabs.cumulative'),
          type: 'line',
          data,
          valueFormatter: fmt,
        },
      ]}
      empty={emptyBox(t('admin.subscriptions.empty'))}
    />
  );
}

/** Comparação por moeda: total semanal (realizado + previsto), 1 série por moeda.
 * Sem FX — valores em unidades de cada moeda, eixo só como referência relativa. */
function ByCurrencyChart({
  buckets,
  currencies,
}: {
  buckets: ReceivablesWeekBucket[];
  currencies: Currency[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const series = currencies.map((c) => ({
    title: c,
    type: 'line' as const,
    data: buckets.map((b) => {
      const tot = bucketTotals(b, c);
      return { x: b.periodKey, y: (tot?.realizedCents ?? 0) + (tot?.forecastCents ?? 0) };
    }),
  }));
  return (
    <LineChart
      {...CHART_COMMON}
      ariaLabel={t('admin.subscriptions.tabs.byCurrency')}
      i18nStrings={{ yTickFormatter: (v): string => String(Math.round(v / 100)) }}
      series={series}
      empty={emptyBox(t('admin.subscriptions.empty'))}
    />
  );
}

/** Previsto por ciclo: mensal × anual (barras empilhadas) por semana. */
function ByCycleChart({
  rows,
  currency,
  fmt,
}: {
  rows: ReceivablesWeekBucket[];
  currency: Currency;
  fmt: (v: number) => string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const monthly = rows.map((b) => ({
    x: b.periodKey,
    y: bucketTotals(b, currency)?.forecastMonthlyCents ?? 0,
  }));
  const yearly = rows.map((b) => ({
    x: b.periodKey,
    y: bucketTotals(b, currency)?.forecastYearlyCents ?? 0,
  }));
  return (
    <BarChart
      {...CHART_COMMON}
      stackedBars
      ariaLabel={t('admin.subscriptions.tabs.byCycle')}
      i18nStrings={{ yTickFormatter: fmt }}
      series={[
        { title: t('admin.subscriptions.cycle.monthly'), type: 'bar', data: monthly },
        { title: t('admin.subscriptions.cycle.yearly'), type: 'bar', data: yearly },
      ]}
      empty={emptyBox(t('admin.subscriptions.empty'))}
    />
  );
}
